import PropTypes from 'prop-types';
import React, {createContext, useContext, useEffect, useState} from 'react';
import type {ThemeMode} from 'Theme/buildAntdTheme';

/**
 * App-wide light/dark mode toggle. Persisted to localStorage so it survives
 * reloads. Every first visit defaults to 'light' regardless of the OS/browser
 * color-scheme preference — the toggle below is still there for anyone who
 * wants dark mode, it just isn't picked automatically.
 */

const STORAGE_KEY = 'ast-theme-mode';

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  toggleMode: () => {},
  setMode: () => {},
});

const getInitialMode = (): ThemeMode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch (error) {
    // localStorage unavailable (private browsing, disabled storage, etc.) — fall through.
  }
  return 'light';
};

const ThemeModeProvider = ({children}) => {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (error) {
      // ignore persistence failures — mode still works for the current session
    }
  }, [mode]);

  const toggleMode = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));

  return (
    <ThemeModeContext.Provider value={{mode, toggleMode, setMode}}>
      {children}
    </ThemeModeContext.Provider>
  );
};

const useThemeMode = (): ThemeModeContextValue => useContext(ThemeModeContext);

ThemeModeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {ThemeModeProvider, ThemeModeContext, useThemeMode};
